import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import AddIcon from '@mui/icons-material/Add';
import { getPricingRules, savePricingRule, deletePricingRule } from '../modules/standardbasedpricingdata';
import { getContractData } from '../modules/contractdata';
import { cloneDeep } from 'lodash';
import CpStandardPricingRow from './cpstandardpricingrow';
import { standardClasses } from './standardclasses';
import ConfirmationDialog from '../common/confirmationdialog';
import PricingRule from '../domain/pricingrule';
import ObjectIdGenerator from '../modules/objectidgenerator';

const styles = {
    table: { mt: '0.5em' },
    addButton: { m: 1 },
    overlayContainer: { position: 'relative' },
    overlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255,255,255,0.6)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'auto',
    },
};

export default function ContractPricesStandardPricing(props) {
    const dispatch = useDispatch();
    const products = useSelector((state) => state.productdata.filterProducts);
    const pricingRules = useSelector((state) => state.standardbasedpricingdata.pricingRules);
    const pricingRulesFetching = useSelector((state) => state.standardbasedpricingdata.fetchingData);

    const [prodPreviews, setProdPreviews] = React.useState({});
    const [rowEdits, setRowEdits] = React.useState({});
    const [materials, setMaterials] = React.useState({});
    const [newRule, setNewRule] = React.useState(null);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);
    const [itemToDelete, setItemToDelete] = React.useState(null);
    const [deletingRule, setDeletingRule] = React.useState(null);

    const { t } = useTranslation();

    // No grouping needed - rules already have materials as an array
    const displayRules = useMemo(() => {
        if (!pricingRules) return [];

        return pricingRules.map((rule) => ({
            ...rule,
            // For UI compatibility, ensure materials is always an array
            materials: Array.isArray(rule.materials) ? rule.materials : [rule.materials].filter(Boolean),
        }));
    }, [pricingRules]);

    const getProductCount = React.useCallback(
        (rule) => {
            if (!products) {
                return 0;
            }
            let totalCount = 0;
            for (const material of rule.materials) {
                const matches = products.filter((p) => {
                    return (
                        p.type === rule.type &&
                        p.productName.startsWith(material) &&
                        p.standardClass === rule.standardClass
                    );
                });
                totalCount += matches.length;
            }
            return totalCount;
        },
        [products]
    );

    useEffect(() => {
        if (!pricingRules && !pricingRulesFetching) {
            getPricingRules()(dispatch);
        }
    }, [pricingRules, pricingRulesFetching, dispatch]);

    // Build materials map when products change
    useEffect(() => {
        if (products && products.length > 0) {
            // Build materials map for all product types
            const materialsMap = {};

            // Product types to skip when matching materials (case insensitive)
            const skipTypes = ['other'];

            // First, initialize all product types with empty sets
            products.forEach((item) => {
                if (item.type && !materialsMap[item.type]) {
                    materialsMap[item.type] = new Set();
                }
            });

            // Then, add materials for products with percentage values (skip certain types)
            products.forEach((item) => {
                if (
                    item.type &&
                    item.productName &&
                    !skipTypes.some((skip) => skip.toLowerCase() === item.type.toLowerCase())
                ) {
                    // Find percentage value in product name (e.g., "65%") - must be digits directly followed by % with no space
                    const percentageMatch = item.productName.match(/\d{2}%/);
                    if (percentageMatch) {
                        const percentageIndex = item.productName.indexOf(percentageMatch[0]);
                        const material = item.productName.slice(0, percentageIndex + percentageMatch[0].length).trim();
                        materialsMap[item.type].add(material);
                    }
                    // Skip products without percentage value
                }
            });

            // Convert sets to arrays
            const finalMaterials = {};
            Object.keys(materialsMap).forEach((type) => {
                finalMaterials[type] = [...materialsMap[type]];
            });
            setMaterials(finalMaterials);
        }
    }, [products]);

    // Update previews in real-time when rules, edits, or newRule change
    useEffect(() => {
        if (products && products.length > 0 && displayRules) {
            const previews = {};
            for (const row of displayRules) {
                previews[row.id] = getProductCount(row);
            }
            // Calculate previews for edited rows
            Object.keys(rowEdits).forEach((id) => {
                previews[id] = getProductCount(rowEdits[id]);
            });
            setProdPreviews(previews);
        }
    }, [products, displayRules, rowEdits, getProductCount]);

    useEffect(() => {
        if (newRule) {
        }
    }, [newRule]);

    const rowChanged = (row) => {
        const existingRule = displayRules.find((r) => r.id === row.id);
        if (existingRule) {
            const newEdits = cloneDeep(rowEdits);
            newEdits[row.id] = row;
            setRowEdits(newEdits);
        } else {
            setNewRule(row);
        }
    };

    const rowClick = (id) => {
        if (!rowEdits[id]) {
            const edits = {};
            edits[id] = displayRules.find((r) => r.id === id);
            setRowEdits(edits);
        }
    };

    const cancelRowEdit = () => {
        setRowEdits({});
    };

    const saveModifiedRow = async (id) => {
        const rule = rowEdits[id];
        await savePricingRule(rule)(dispatch);
        getContractData()(dispatch);
        setRowEdits({});
    };

    const cancelNewRowEdit = () => {
        setNewRule(null);
    };

    const saveNewRow = async () => {
        await savePricingRule(newRule)(dispatch);
        setNewRule(null);
    };

    const deleteModifiedRow = async (id) => {
        setItemToDelete(displayRules.find((r) => r.id === id));
        setConfirmDeleteOpen(true);
    };

    const doDelete = async () => {
        if (!itemToDelete) return;
        setDeletingRule(itemToDelete.id);
        try {
            await deletePricingRule(itemToDelete.id)(dispatch);
            setRowEdits({});
            setItemToDelete(null);
            setConfirmDeleteOpen(false);
        } finally {
            setDeletingRule(null);
        }
    };

    return (
        <Grid container sx={styles.overlayContainer}>
            {deletingRule && (
                <Box sx={styles.overlay} aria-hidden="true">
                    <CircularProgress />
                </Box>
            )}
            <Grid item xs={12}>
                <Table sx={styles.table}>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('product.type')}</TableCell>
                            <TableCell>{t('survey.material')}</TableCell>
                            <TableCell>{t('product.standardClass')}</TableCell>
                            <TableCell>{t('product.percentageDiscount')}</TableCell>
                            <TableCell>{t('general.products') + ' ' + t('pcs')}</TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!pricingRules && (
                            <TableRow>
                                <TableCell colSpan={6}>{t('general.loading')}</TableCell>
                            </TableRow>
                        )}
                        {displayRules &&
                            displayRules.map((row) => (
                                <CpStandardPricingRow
                                    key={row.id}
                                    row={rowEdits[row.id] ? rowEdits[row.id] : row}
                                    edit={rowEdits[row.id]}
                                    materials={materials}
                                    standardClasses={standardClasses()}
                                    onClick={rowClick}
                                    rowChanged={rowChanged}
                                    prodPreviews={prodPreviews[row.id]}
                                    cancel={cancelRowEdit}
                                    save={saveModifiedRow}
                                    deleteRule={deleteModifiedRow}
                                />
                            ))}
                        {newRule && (
                            <CpStandardPricingRow
                                row={newRule}
                                edit={true}
                                materials={materials}
                                standardClasses={standardClasses()}
                                onClick={rowClick}
                                rowChanged={rowChanged}
                                prodPreviews={0}
                                cancel={cancelNewRowEdit}
                                save={saveNewRow}
                            />
                        )}
                    </TableBody>
                </Table>
            </Grid>
            <Grid item>
                <Button
                    disabled={newRule || !!deletingRule}
                    sx={styles.addButton}
                    startIcon={<AddIcon />}
                    variant="contained"
                    color="primary"
                    name="btn-add-new-rule"
                    onClick={async () => {
                        // Create a new rule with empty materials array
                        const rule = new PricingRule();
                        rule.id = await ObjectIdGenerator.newId();
                        setNewRule(rule);
                        setRowEdits({});
                    }}></Button>
            </Grid>
            <ConfirmationDialog
                open={confirmDeleteOpen}
                confirm={doDelete}
                cancel={() => setConfirmDeleteOpen(false)}
                confirmText={t('buttons.remove')}>
                {t('product.confirmDeletePricingRule', {
                    ruleDesc: itemToDelete
                        ? `${itemToDelete.type} ${itemToDelete.materials.join(', ')}, standard:${itemToDelete.standardClass}, -${itemToDelete.discount}%`
                        : '',
                })}
            </ConfirmationDialog>
        </Grid>
    );
}
